"use client";
import { useEffect, useMemo, useState } from "react";

import type { SurveyQuestion } from "@/types/survey";
import { CheckboxGroup, InputText } from "@/design-system";
import {
  SELECT_OTHER_LABEL,
  SELECT_OTHER_VALUE,
  isOtherAnswer,
  isSelectOtherEnabled,
  otherValueFromAnswer,
  toOtherAnswer,
} from "@/lib/selectOtherOption";
import { getMultiSelectMaxSelections } from "@/lib/multiSelectOptions";

interface MultiSelectQuestionProps {
  question: SurveyQuestion;
  brandColor?: string;
  isSelected?: (
    question: SurveyQuestion,
    candidate: string | number,
  ) => boolean;
  onAnswerChange?: (question: SurveyQuestion, value: string | string[]) => void;
  isPreview?: boolean;
  selectedValue?: string | number | string[];
  onPreviewAnswerChange?: (
    questionId: string,
    value: string | number | string[],
  ) => void;
  uiStrings?: {
    otherOption: string;
    pleaseSpecify: string;
    selectUpTo: (count: number) => string;
  };
}

export default function MultiSelectQuestion({
  question,
  isSelected: _isSelected,
  onAnswerChange,
  isPreview = false,
  selectedValue,
  onPreviewAnswerChange,
  uiStrings,
}: MultiSelectQuestionProps) {
  const choiceItems = useMemo(
    () =>
      (question.choices || []).map((c) => ({
        value: c.value,
        label: c.text,
      })),
    [question.choices],
  );
  const allowOther = isSelectOtherEnabled(question);
  const otherLabel = uiStrings?.otherOption ?? SELECT_OTHER_LABEL;
  const maxSelections = getMultiSelectMaxSelections(question);
  const selectedValues = useMemo(() => {
    if (Array.isArray(selectedValue)) return selectedValue.map(String);
    if (isPreview || typeof selectedValue !== "undefined") return [];
    return choiceItems
      .filter((i) => (_isSelected ? _isSelected(question, i.value) : false))
      .map((i) => String(i.value));
  }, [choiceItems, isPreview, _isSelected, question, selectedValue]);
  const selectedOtherAnswer = allowOther
    ? selectedValues.find((value) => isOtherAnswer(value))
    : undefined;
  const [otherInputValue, setOtherInputValue] = useState(
    otherValueFromAnswer(selectedOtherAnswer),
  );

  useEffect(() => {
    setOtherInputValue(otherValueFromAnswer(selectedOtherAnswer));
  }, [selectedOtherAnswer]);

  const checkboxValues = useMemo(() => {
    const choiceValueSet = new Set(choiceItems.map((item) => String(item.value)));
    const values = selectedValues.filter((value) => choiceValueSet.has(value));
    if (selectedOtherAnswer) values.push(SELECT_OTHER_VALUE);
    return values;
  }, [choiceItems, selectedOtherAnswer, selectedValues]);
  const limitReached =
    maxSelections !== null && checkboxValues.length >= maxSelections;
  const items = useMemo(() => {
    const baseItems = allowOther
      ? [
          ...choiceItems,
          { value: SELECT_OTHER_VALUE, label: otherLabel },
        ]
      : choiceItems;
    if (!limitReached) return baseItems;

    const selectedSet = new Set(checkboxValues);
    return baseItems.map((item) => ({
      ...item,
      disabled: !selectedSet.has(String(item.value)),
    }));
  }, [allowOther, checkboxValues, choiceItems, limitReached, otherLabel]);

  const commitValues = (next: string[]) => {
    if (isPreview && onPreviewAnswerChange) {
      onPreviewAnswerChange(question.id, next);
      return;
    }
    if (onAnswerChange) {
      onAnswerChange(question, next);
    }
  };

  const buildNextValues = ({
    checkedValues,
    otherText,
  }: {
    checkedValues: string[];
    otherText: string;
  }) => {
    const next = checkedValues.filter((value) => value !== SELECT_OTHER_VALUE);
    if (checkedValues.includes(SELECT_OTHER_VALUE)) {
      next.push(toOtherAnswer(otherText, { allowBlank: true }));
    }
    return next;
  };

  const applySelectionLimit = (next: string[]) => {
    if (maxSelections === null || next.length <= maxSelections) return next;

    const previousSet = new Set(checkboxValues);
    const preserved = next.filter((value) => previousSet.has(value));
    if (preserved.length >= maxSelections) {
      return preserved.slice(0, maxSelections);
    }

    const added = next.filter((value) => !previousSet.has(value));
    return [...preserved, ...added].slice(0, maxSelections);
  };

  const handleValuesChangeUnknown = (vals: unknown[] | null) => {
    const checkedValues = applySelectionLimit((vals || []).map(String));
    commitValues(buildNextValues({ checkedValues, otherText: otherInputValue }));
  };

  const handleOtherTextChange = (event: { target: { value: string } }) => {
    const next = event.target.value;
    setOtherInputValue(next);
    commitValues(buildNextValues({ checkedValues: checkboxValues, otherText: next }));
  };

  return (
    <div className="mb-4">
      <div
        className="text-sm font-medium text-left"
        style={{
          color: "var(--sl-text, var(--color-alias-neutral-darkest))",
          marginBottom:
            maxSelections === null
              ? "calc(var(--spacing) * 4)"
              : "calc(var(--spacing) * 1)",
        }}
      >
        {question.text}
      </div>
      {maxSelections !== null ? (
        <div
          className="text-xs text-left"
          style={{
            color: "var(--sl-text-muted, var(--color-alias-neutral-dark))",
            marginBottom: "calc(var(--spacing) * 4)",
          }}
        >
          {uiStrings?.selectUpTo(maxSelections) ?? `(Select up to ${maxSelections})`}
        </div>
      ) : null}
      <CheckboxGroup
        label=""
        items={items}
        values={checkboxValues}
        onValuesChange={handleValuesChangeUnknown}
        orientation="vertical"
      />
      {selectedOtherAnswer ? (
        <div className="mt-2">
          <InputText
            type="text"
            placeholder={uiStrings?.pleaseSpecify ?? "Please specify"}
            value={otherInputValue}
            onChange={handleOtherTextChange}
            fullWidth
          />
        </div>
      ) : null}
    </div>
  );
}
